import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse } from "src/types";

import { AdminBuilder } from "./tags";

const jsonHeaders = { "content-type": "application/json" };

export type LicenseStatistics = {
    totalLicenses: number;
    totalAssociatedLicenses: number;
    totalRevokedLicenses: number;
};

export type LicenseInspection = {
    serverData: string;
    serverNonce: string;
    deviceData: string;
    deviceNonce: string;
};

const byKey = (searchTerm: string, suffix = "") => `${encodeURIComponent(searchTerm)}${suffix}`;

type GrantLicensesParams = {
    userEmail: string;
    groupId: number;
    grantCount: number;
};

const licenses = (builder: AdminBuilder) => ({
    getLicenseStatistics: builder.query<LicenseStatistics, void>({
        providesTags: ["LicenseStatistics"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<LicenseStatistics>) => response.model,
        query: async () => {
            const base = await RouteService.getAdminLicensesRoute();
            return { url: `${base}statistics` };
        },
    }),

    grantLicenses: builder.mutation<void, GrantLicensesParams>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.grant.toast.error")),
        query: async ({ userEmail, groupId, grantCount }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return {
                url: base,
                method: "POST",
                data: { email: userEmail, groupId, grantCount },
                headers: jsonHeaders,
            };
        },
    }),

    moveLicense: builder.mutation<void, { licenseKey: string; groupId: number }>({
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.move.toast.error")),
        query: async ({ licenseKey, groupId }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return {
                url: `${base}${byKey(licenseKey, "/group")}`,
                method: "PATCH",
                data: { groupId },
                headers: jsonHeaders,
            };
        },
    }),

    revokeLicense: builder.mutation<void, { licenseKey: string; reason: string }>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.revoke.toast.error")),
        query: async ({ licenseKey, reason }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return {
                url: `${base}${byKey(licenseKey, "/revocation")}`,
                method: "POST",
                data: { reason },
                headers: jsonHeaders,
            };
        },
    }),

    reviveLicense: builder.mutation<void, { licenseKey: string; groupId: number; reason: string }>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.revive.toast.error")),
        query: async ({ licenseKey, groupId, reason }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return { url: `${base}${byKey(licenseKey, "/revocation")}`, method: "DELETE", params: { groupId, reason } };
        },
    }),

    attachLicense: builder.mutation<void, { licenseKey: string; deviceSerial: string }>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.attach.toast.error")),
        query: async ({ licenseKey, deviceSerial }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return {
                url: `${base}${byKey(licenseKey, "/device")}`,
                method: "POST",
                data: { deviceSerial },
                headers: jsonHeaders,
            };
        },
    }),

    detachLicense: builder.mutation<void, { licenseKey: string; reason: string }>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.detach.toast.error")),
        query: async ({ licenseKey, reason }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return { url: `${base}${byKey(licenseKey, "/device")}`, method: "DELETE", params: { reason } };
        },
    }),

    inspectLicense: builder.mutation<LicenseInspection, { licenseKey: string }>({
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.inspect.toast.error")),
        transformResponse: (response: ApiResponse<LicenseInspection>) => response.model,
        query: async ({ licenseKey }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return { url: `${base}${byKey(licenseKey, "/inspection")}` };
        },
    }),

    deleteLicenses: builder.mutation<void, { licenseKey: string; reason: string }>({
        invalidatesTags: invalidateOnSuccess("LicenseStatistics"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("pages:adminLicensesPage.delete.toast.error")),
        query: async ({ licenseKey, reason }) => {
            const base = await RouteService.getAdminLicensesRoute();
            return { url: `${base}${byKey(licenseKey)}`, method: "DELETE", params: { reason } };
        },
    }),
});

export default licenses;
